const KEY = 'sh_token';
const USER_KEY = 'sh_user';
const IMP_KEY = 'sh_impersonator';

export const AUTH_EVENT = 'sh-auth';

function notifyAuth() {
  try {
    window.dispatchEvent(new Event(AUTH_EVENT));
  } catch {
    /* ignore */
  }
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string, user?: unknown): void {
  const prev = getToken();
  try {
    localStorage.setItem(KEY, token);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* ignore */
  }
  if (prev !== token) notifyAuth();
}

export function clearToken(): void {
  const had = !!getToken();
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(IMP_KEY);
  } catch {
    /* ignore */
  }
  if (had) notifyAuth();
}

export function getUser(): { id: string; email: string; name?: string; role?: string } | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function startImpersonation(token: string, user?: unknown): void {
  try {
    if (!sessionStorage.getItem(IMP_KEY)) {
      sessionStorage.setItem(IMP_KEY, JSON.stringify({ token: getToken(), user: getUser() }));
    }
  } catch {
    /* ignore */
  }
  setToken(token, user);
}

export function stopImpersonation(): boolean {
  try {
    const raw = sessionStorage.getItem(IMP_KEY);
    sessionStorage.removeItem(IMP_KEY);
    if (!raw) return false;
    const saved = JSON.parse(raw) as { token?: string; user?: unknown };
    if (saved.token) {
      setToken(saved.token, saved.user);
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

export function isImpersonating(): boolean {
  try {
    return !!sessionStorage.getItem(IMP_KEY);
  } catch {
    return false;
  }
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

// authFetch adds the Bearer token when present and returns the response.
export async function authFetch(input: string, init: Parameters<typeof fetch>[1] = {}): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !headers.has('Content-Type') && !isForm) headers.set('Content-Type', 'application/json');
  return fetch(input, { ...init, headers });
}

// apiJSON wraps authFetch + JSON parsing + 401 redirect.
export async function apiJSON<T>(input: string, init: Parameters<typeof fetch>[1] = {}): Promise<T> {
  const res = await authFetch(input, init);
  if (res.status === 401 && !location.pathname.startsWith('/login')) {
    clearToken();
    location.assign('/login');
  }
  if (!res.ok) {
    let msg = `request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}
